import {ItemModelType, WorkspaceModelType} from "@stores/WorkspaceStore";
import {Item} from "../../@types/Item";

import {debounce} from "../../utils/debounce";
import {RecursionOnHierarchy} from "../../utils/errors/RecursionOnHierarchy";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {UpdateItemChildIdsSet} from "./updateItemUndoLogic";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {captureMessageWrapper} from "../../utils/errors/captureMessage";
import {UndoableUpdateItemChildIdsAdd} from "../workspace/updateWorkspaceUndoLogic";
import {getChangeHistoryForItem} from "../../sync/itemSync";

import {queryGqlApi} from "../../graphql/queryGqlApi";
import {updateItem as updateItemGQL} from "../../graphql/mutations";
import {itemToItemServer} from "@stores/convertors/itemToItemServer";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import { DataSourceManager } from "../../stores/dataSources/DataSourceManager";

export const updateItemDB = async (
    services: CrudServices,
    dsManager: DataSourceManager,
    wsId: string,
    itemId: string,
    fieldsToUpdate: Partial<Item>,
): Promise<boolean> => {
    const updateTimeISO = new Date().toISOString();
    const currentItemDB = dsManager.getItemsDataSource(wsId)!.allRows.find(item => item.id === itemId);

    // Pin the change in history
    getChangeHistoryForItem(itemId).pinStep(updateTimeISO);

    const serverFieldsToUpdate = itemToItemServer({
        ...fieldsToUpdate,
        updatedAt: updateTimeISO
    })

    await queryGqlApi(
        updateItemGQL,
        {
            input: {
                id: itemId,
                _version: currentItemDB?._version || 0,
                ...serverFieldsToUpdate,
            }
        },
        services.dataClientService,
        services.authService,
        'main'
    );
    /* TODO implement version mismatch update issue
    (server will just return current record, we need to manually  check if it contains our change
     - if not request - sync and again)
  */

    return true;
};


export function updateItemChildIdsSet(
    services: CrudServices,
    dsManager: DataSourceManager,
    targetId: string,
    childIds: string[],
    workspace: WorkspaceModelType,
    dbChangeQueue: Promise<any> = Promise.resolve(),
    undoableActionGroup?: UndoableActionGroup
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UpdateItemChildIdsSet(services, dsManager, targetId, childIds, workspace, dbChangeQueue);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _updateItemChildIdsSet(services, dsManager, targetId, childIds, workspace, dbChangeQueue);
    }
}

export function _updateItemChildIdsSet(
    services: CrudServices,
    dsManager: DataSourceManager,
    targetId: string,
    childIds: string[],
    workspace: WorkspaceModelType,
    dbChangeQueue: Promise<any> = Promise.resolve()
) {
    const targetItemMST = workspace.items.get(targetId)!;

    if ([...new Set(childIds)].length !== childIds.length) {
        captureMessageWrapper(`attempt to set duplicate childs to ${targetId}, childIds: ${JSON.stringify(childIds)} \n ${new Error().stack}`);
    }

    const newChildIds: string[] = [];
    const toBeRemovedChildIds: string[] = [];
    const existingChildIds = targetItemMST!.childIds.toJSON();
    for (const childId of existingChildIds) {
        if (!childIds.includes(childId)) {
            toBeRemovedChildIds.push(childId);
        }
    }
    for (const childId of childIds) {
        if (targetId === childId) {
            throw new RecursionOnHierarchy(targetId, childId);
        }
        if (!existingChildIds.includes(childId)) {
            newChildIds.push(childId);
        }
    }

    targetItemMST.setChildIds(childIds);

    const updateChildParentIds = () => {
        const affectedParentIdsUpdate: Promise<any>[] = [];
        for (const childId of newChildIds) {
            affectedParentIdsUpdate.push(_updateParentIdsAdd(services, dsManager, workspace.id, workspace.items.get(childId)!, targetId));
        }
        for (const childId of toBeRemovedChildIds) {
            affectedParentIdsUpdate.push(_updateParentIdsRemove(services, dsManager, workspace.id, workspace.items.get(childId)!, targetId));
        }
        return Promise.all(affectedParentIdsUpdate);
    };

    return dbChangeQueue.then(() => {
        return Promise.all([
            updateItemDB(services, dsManager, workspace.id, targetId, {
                childIds: childIds,
            }),
            updateChildParentIds(),
        ]);
    });
}

export function updateItemChildIdsAdd(
    services: CrudServices,
    dsManager: DataSourceManager,
    mstItem: ItemModelType,
    childId: string,
    workspace: WorkspaceModelType,
    index?: number,
    undoableActionGroup?: UndoableActionGroup,
) {
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableUpdateItemChildIdsAdd(services, dsManager, mstItem, childId, workspace, index);
        return undoableActionGroup.add(undoableAction);
    } else {
        return _updateItemChildIdsAdd(services, dsManager, mstItem, childId, workspace, index);
    }
}

export function _updateItemChildIdsAdd(
    services: CrudServices,
    dsManager: DataSourceManager,
    mstItem: ItemModelType,
    childId: string,
    workspace: WorkspaceModelType,
    index?: number,
) {
    if (mstItem.childIds.indexOf(childId) > -1) {
        console.warn('attempt to add duplicate child id');
        return Promise.resolve();
    }

    if (mstItem.id === childId) {
        throw new RecursionOnHierarchy(mstItem.id, childId);
    }

    mstItem.addChild(childId, index);

    const updateParentIdsWait = _updateParentIdsAdd(services, dsManager, workspace.id, workspace.items.get(childId)!, mstItem.id);

    return Promise.all([
        updateItemDB(services, dsManager, workspace.id, mstItem.id, {
            childIds: mstItem.childIds.toJSON(),
        }),
        updateParentIdsWait
    ]);
}

export function updateItemChildIdsRemove(
    services: CrudServices,
    dsManager: DataSourceManager,
    targetItemId: string, 
    childIdToRemove: string, 
    workspace: WorkspaceModelType
): Promise<any> {
    const itemMST = workspace.items.get(targetItemId)!;
    if (!itemMST) {
        /* on bulk delete there is case when parent already removed as well and we remove childs (which leads to this function call) */
        return Promise.resolve();
    }
    itemMST.removeChild(childIdToRemove);

    const updateParentIdsChange = _updateParentIdsRemove(services, dsManager, workspace.id, workspace.items.get(childIdToRemove)!, itemMST.id).catch(logErrorToSentry);

    return Promise.all([
        updateItemDB(services, dsManager, workspace.id, targetItemId, {
            childIds: itemMST.childIds.toJSON(),
        }),
        updateParentIdsChange
    ]);
}

/* should only be called innerly form updateItemChildIdsAdd */
function _updateParentIdsAdd(
    services: CrudServices,
    dsManager: DataSourceManager,
    workspaceId: string, 
    mstItem: ItemModelType, 
    parentId: string
) {
    if (mstItem.parentIds.includes(parentId)) {
        return Promise.resolve();
    }
    mstItem.addParent(parentId);
    return updateItemDB(services, dsManager, workspaceId, mstItem.id, {
        parentIds: mstItem.parentIds.toJSON(),
    })
}

async function _updateParentIdsRemove(
    services: CrudServices,
    dsManager: DataSourceManager,
    workspaceId: string, 
    mstItem: ItemModelType, 
    parentId: string
) {
    if (!mstItem.parentIds.includes(parentId)) {
        return Promise.resolve();
    }
    mstItem.removeParent(parentId);

    return updateItemDB(services, dsManager, workspaceId, mstItem.id, {
        parentIds: mstItem.parentIds.toJSON(),
    })
}

const debounceMap = new WeakMap<ItemModelType, ReturnType<typeof debounce>>();

export function updateItemName(
    services: CrudServices,
    dsManager: DataSourceManager,
    mstItem: ItemModelType, 
    newName: string
) {
    mstItem.setName(newName);

    if (!debounceMap.has(mstItem)) {
        debounceMap.set(mstItem, debounce(
            (newName: string) => {
                updateItemDB(services, dsManager, mstItem.workspaceId, mstItem.id, {name: newName});
            },
            400
        ));
    }
    const debouncedFn = debounceMap.get(mstItem);
    debouncedFn?.(newName);
}
